import { AliasTypeAndValue, EntityType, EntityTypeEnum, TradingEntityAliasType, TradingEntityMeta, WorkflowActions } from 'api'
import { Locale, Translation } from 'locale'
import { isNil } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createWorkflowReferenceDataEntitySelector, workflowTradingEntityMetasGetterSelector } from 'selectors/workflowSelectors'
import { GlobalState } from 'store'
import { EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { useParametricSelector } from 'utils'
import { DialogField, DialogRow, TextInputField } from 'view/components/inputs'
import { SelectboxField } from 'view/components/options'
import { Groupbox } from 'view/components/panels'
import { DialogWindow } from 'view/components/panels/DialogWindow'
import { SubjectSelectboxField } from 'view/shared/entity/SubjectTradingEntitySelectbox'

export interface TradingEntityAliasesAddDialogProps {
  onClose?: () => unknown
  workflowInfo: WorkflowInfo
  ownerTradingEntityId: string
}

interface WorkflowInfo {
  workflowId: string
  metaId?: string | null
  index: number | null
}

const workflowIdSelector = (_state: GlobalState, workflowInfo: WorkflowInfo) => workflowInfo.workflowId ?? ''
const metaEntityIdSelector = (_state: GlobalState, workflowInfo: WorkflowInfo) => workflowInfo.metaId ?? ''

export const TradingEntityAliasesPanelDialog = (props: TradingEntityAliasesAddDialogProps) => {
  const [workflowInfo, setWorkflowInfo] = useState<WorkflowInfo>({
    workflowId: props.workflowInfo.workflowId,
    metaId: props.workflowInfo.metaId ?? null,
    index: props.workflowInfo.index ?? null
  })
  const workflowReference = useParametricSelector(
    createWorkflowReferenceDataEntitySelector(EntityType.TRADING_ENTITY_META, workflowIdSelector, metaEntityIdSelector),
    workflowInfo
  )

  const validationIssues = workflowReference?.validationIssues ?? []
  const dispatch = useDispatch()
  const getWorkflowMetas = useSelector(workflowTradingEntityMetasGetterSelector)
  const [subjectTradingEntityId, setSubjectTradingEntityId] = useState<string | null>(null)
  const [aliasType, setAliasType] = useState<TradingEntityAliasType | null>(null)
  const [aliasName, setAliasName] = useState<string | null>(null)
  const isEditing = !isNil(props.workflowInfo.index)
  const backUpMetaRef = useRef<TradingEntityMeta>()

  useEffect(
    function loadEditModeOnMount() {
      if (props.workflowInfo && !isNil(props.workflowInfo.index)) {
        const index = props.workflowInfo.index
        const alias = workflowReference?.data?.aliasTypeAndValues?.[index]

        if (alias && !subjectTradingEntityId && !aliasType && !aliasName) {
          backUpMetaRef.current = workflowReference.data
          setSubjectTradingEntityId(workflowReference.data.subjectTradingEntityId)
          setAliasType(alias.type)
          setAliasName(alias.value)
        }
      }
    },
    [subjectTradingEntityId, aliasType, aliasName, props.workflowInfo, workflowReference?.data, isEditing]
  )

  function handleRestore() {
    if (backUpMetaRef.current && workflowReference?.data) {
      dispatch(
        WorkflowActions.createEntityUpdateReferenceData<TradingEntityMeta>({
          entityId: workflowReference.data.id,
          entityType: EntityTypeEnum.TRADING_ENTITY_META,
          updatedEntity: { ...backUpMetaRef.current },
          workflowId: workflowInfo.workflowId
        })
      )
      backUpMetaRef.current = undefined
    } else {
      workflowReference?.data.id &&
        dispatch(
          WorkflowActions.createEntityDeleteReferenceData(
            [{ entityId: workflowReference.data.id, entityType: EntityTypeEnum.TRADING_ENTITY_META }],
            workflowInfo.workflowId
          )
        )
    }
  }

  function updateWorkflowWithAlias(aliasTypeAndValue: AliasTypeAndValue) {
    if (!workflowReference?.data || isNil(workflowInfo.index)) return

    const aliases = workflowReference.data.aliasTypeAndValues.slice() ?? []
    aliases[workflowInfo.index] = { ...aliasTypeAndValue }

    dispatch(
      WorkflowActions.createEntityUpdateReferenceData({
        entityId: workflowReference.data.id,
        entityType: EntityTypeEnum.TRADING_ENTITY_META,
        updatedEntity: {
          ...workflowReference.data,
          ownerTradingEntityId: props.ownerTradingEntityId,
          subjectTradingEntityId,
          aliasTypeAndValues: aliases
        },
        workflowId: workflowInfo.workflowId
      })
    )
  }

  function onSelectCounterparty(counterpartyId: string) {
    if (subjectTradingEntityId) {
      handleRestore()
    }

    const workflowMetas = getWorkflowMetas(workflowInfo.workflowId)

    const metaFound = workflowMetas.find(
      m => m.subjectTradingEntityId === counterpartyId && m.ownerTradingEntityId === props.ownerTradingEntityId
    )

    const draftAlias: AliasTypeAndValue | null = aliasName || aliasType ? { value: aliasName, type: aliasType } : null

    if (!metaFound) {
      dispatch(
        WorkflowActions.createAddToExistingWorkflow(
          workflowInfo.workflowId,
          EntityTypeEnum.TRADING_ENTITY_META,
          (_workflowId: string, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
            const id = newlyAddedEntityIdAndTypes[EntityTypeEnum.TRADING_ENTITY_META]

            setWorkflowInfo(info => ({ ...info, metaId: id, index: 0 }))

            dispatch(
              WorkflowActions.createEntityUpdateReferenceData({
                entityId: id,
                entityType: EntityTypeEnum.TRADING_ENTITY_META,
                workflowId: workflowInfo.workflowId,
                updatedEntity: {
                  id,
                  ownerTradingEntityId: props.ownerTradingEntityId,
                  subjectTradingEntityId: counterpartyId,
                  primaryCoverUserId: null,
                  primerBrokerId: null,
                  secondaryCoverUserId: null,
                  aliasTypeAndValues: draftAlias ? [draftAlias] : []
                }
              })
            )
          }
        )
      )
    } else {
      const aliases = draftAlias ? [...metaFound.aliasTypeAndValues, draftAlias] : metaFound.aliasTypeAndValues

      dispatch(
        WorkflowActions.createEntityUpdateReferenceData({
          entityId: metaFound.id,
          entityType: EntityTypeEnum.TRADING_ENTITY_META,
          workflowId: workflowInfo.workflowId,
          updatedEntity: {
            ...metaFound,
            aliasTypeAndValues: aliases
          }
        })
      )

      backUpMetaRef.current = { ...metaFound }
      setWorkflowInfo(info => ({ ...info, metaId: metaFound.id, index: metaFound.aliasTypeAndValues?.length ?? 0 }))
    }

    setSubjectTradingEntityId(counterpartyId)
  }

  function onSubmit() {
    if (validationIssues.length) return

    props.onClose?.()
  }

  function onClose() {
    handleRestore()
    props.onClose?.()
  }

  return (
    <DialogWindow
      title={!isEditing ? Translation.TC_ADD_NEW_ALIAS : Translation.TC_EDIT_ALIAS}
      onConfirm={onSubmit}
      confirmButtonEnabled={validationIssues.length === 0 && !!aliasType}
      confirmLabel={Translation.TC_BUTTON_OK}
      onClose={onClose}>
      <Groupbox>
        <DialogRow>
          <DialogField>
            <SubjectSelectboxField
              ownerTradingEntityId={props.ownerTradingEntityId}
              fieldName="subjectTradingEntityId"
              label={Locale.translate(Translation.SC_ALIAS_COUNTERPARTY)}
              validationIssues={validationIssues}
              disabled={isEditing}
              readOnly={isEditing}
              value={subjectTradingEntityId}
              onUpdate={id => {
                onSelectCounterparty(id)
              }}
              dataTestId="DialogWindow-SelectboxInput-Alias-Counterparty"
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <TextInputField
              label={Locale.translate(Translation.TC_ALIAS)}
              fieldName="alias"
              validationIssues={validationIssues}
              disabled={!subjectTradingEntityId}
              value={aliasName}
              onUpdate={name => {
                if (subjectTradingEntityId) {
                  updateWorkflowWithAlias({ type: aliasType, value: name })
                }

                setAliasName(name)
              }}
              dataTestId="DialogWindow-FieldInput-Alias"
            />
          </DialogField>
          <DialogField>
            <SelectboxField
              label={Locale.translate(Translation.SC_ALIAS_TYPE)}
              fieldName="aliasType"
              optionKeys={Object.keys(TradingEntityAliasType) as TradingEntityAliasType[]}
              validationIssues={validationIssues}
              getOptionText={optionKey => {
                return optionKey === TradingEntityAliasType.ALL
                  ? Locale.translate(Translation.UC_ALIAS_TYPE_ALL)
                  : Locale.translate(Translation.UC_ALIAS_TYPE_FICC)
              }}
              value={aliasType}
              onUpdate={(optionKey: TradingEntityAliasType) => {
                if (subjectTradingEntityId) {
                  updateWorkflowWithAlias({ type: optionKey, value: aliasName })
                }

                setAliasType(optionKey)
              }}
              dataTestId="DialogWindow-SelectboxInput-Alias-AliasType"
            />
          </DialogField>
        </DialogRow>
      </Groupbox>
    </DialogWindow>
  )
}
